import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, UtensilsCrossed, Clock, ArrowRight, QrCode } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCE_META = {
  billard: { name: "Billard Tisch", color: "#10b981", count: 10, emoji: "pool" },
  edart: { name: "eDart Automat", color: "#06b6d4", count: 12, emoji: "dart" },
  steeldart: { name: "Steeldart Board", color: "#dc2626", count: 2, emoji: "target" },
};

export default function QRLandingPage() {
  const { type, number } = useParams();
  const [available, setAvailable] = useState(null);
  const num = parseInt(number);
  const meta = RESOURCE_META[type];

  useEffect(() => {
    if (!meta || num < 1 || num > meta.count) return;
    const today = new Date().toISOString().split("T")[0];
    axios.get(`${API}/reservations/available?date=${today}&resource_type=${type}`)
      .then(r => setAvailable(r.data.available || {}))
      .catch(() => {});
  }, [type, num, meta]);

  if (!meta || num < 1 || num > meta.count) {
    return (
      <div className="py-24 text-center px-4">
        <QrCode className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>
          Ungültiger QR-Code
        </h1>
        <p className="text-muted-foreground text-sm mb-6">Diese Ressource wurde nicht gefunden.</p>
        <Link to="/" className="text-primary text-sm hover:underline">Zur Startseite</Link>
      </div>
    );
  }

  const now = new Date();
  const currentHour = now.getHours();
  const nextSlots = available
    ? Object.entries(available)
        .filter(([slot]) => {
          const h = parseInt(slot.split(":")[0]);
          return h >= currentHour || h === 0;
        })
        .slice(0, 3)
    : [];

  return (
    <div className="py-8 md:py-16 px-4 md:px-8" data-testid="qr-landing-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border-2"
            style={{ borderColor: meta.color, backgroundColor: `${meta.color}15` }}>
            <span className="text-3xl font-bold" style={{ fontFamily: 'Oswald', color: meta.color }}>
              {num}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            {meta.name} <span style={{ color: meta.color }}>#{num}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Willkommen am Ninersbillard!</p>
        </div>

        {/* Quick Actions */}
        <div className="space-y-3 mb-8">
          <Link to={`/reservierung`} data-testid="qr-reserve-btn"
            className="flex items-center justify-between bg-card border border-white/5 p-4 hover:border-primary/50 transition-colors duration-200 group">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-5 h-5" style={{ color: meta.color }} />
              <div>
                <p className="font-bold text-sm uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Jetzt reservieren</p>
                <p className="text-xs text-muted-foreground">Tisch oder Automat buchen</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
          </Link>

          <Link to="/preisliste" data-testid="qr-menu-btn"
            className="flex items-center justify-between bg-card border border-white/5 p-4 hover:border-primary/50 transition-colors duration-200 group">
            <div className="flex items-center gap-3">
              <UtensilsCrossed className="w-5 h-5 text-primary" />
              <div>
                <p className="font-bold text-sm uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Speisekarte & Getränke</p>
                <p className="text-xs text-muted-foreground">Preisliste anzeigen</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
          </Link>

          <Link to="/kalender" data-testid="qr-events-btn"
            className="flex items-center justify-between bg-card border border-white/5 p-4 hover:border-primary/50 transition-colors duration-200 group">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-purple-400" />
              <div>
                <p className="font-bold text-sm uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Kommende Events</p>
                <p className="text-xs text-muted-foreground">Termine & Veranstaltungen</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
          </Link>
        </div>

        {/* Current Availability */}
        {nextSlots.length > 0 && (
          <div className="bg-card border border-white/5 p-5 mb-8" data-testid="qr-availability">
            <h2 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald' }}>
              Heutige Verfügbarkeit
            </h2>
            <div className="space-y-2">
              {nextSlots.map(([slot, resources]) => {
                const isAvailable = resources.includes(num);
                return (
                  <div key={slot} className="flex items-center justify-between py-1.5">
                    <span className="text-sm font-medium">{slot} Uhr</span>
                    {isAvailable ? (
                      <Badge className="text-xs bg-green-600 border-0">Frei</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-muted-foreground">Belegt</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
            ninersbillard.at
          </Link>
        </div>
      </div>
    </div>
  );
}
